import { useMemo } from 'react';
import { TIME_FILTER } from 'constants/chart';
import { useGetSurveyHistory } from 'hooks/useGetSurveyHistory';
import type { TimeFilter, ChartDataPoint } from 'types/chart';
import { type SurveyType } from 'types/surveys';
import {
  processHistoryData,
  getLastDaysWithData,
  groupDataByMonths,
  getWeeksOfMonthData,
} from 'utils/chart';

export interface UseChartDataProps {
  timeFilter: TimeFilter;
  selectedWeek: ChartDataPoint | null;
  selectedMonth: { year: number; month: number } | null;
  surveyType: SurveyType;
  surveyId?: string;
}

const addWeekIndex = (data: ChartDataPoint[]): ChartDataPoint[] =>
  data.map((item, index) => ({ ...item, week: index + 1 }));

export const useChartData = ({
  surveyType,
  timeFilter,
  selectedWeek,
  selectedMonth,
}: UseChartDataProps & { surveyType: SurveyType; surveyId?: string }): {
  chartData: ChartDataPoint[];
  isLoading: boolean;
  error: unknown;
} => {
  const { data: historyData, isLoading, error } = useGetSurveyHistory(surveyType);

  const chartData = useMemo(() => {
    if (!historyData) return [];

    if (selectedWeek?.weekData) {
      return addWeekIndex(selectedWeek.weekData);
    }

    const allData = processHistoryData(historyData);
    let processedData: ChartDataPoint[] = [];

    switch (timeFilter) {
      case TIME_FILTER.LAST_7_DAYS:
        processedData = getLastDaysWithData(allData, 7);
        break;
      case TIME_FILTER.LAST_12_MONTHS:
        processedData = groupDataByMonths(allData);
        break;
      case TIME_FILTER.MONTH:
        if (selectedMonth) {
          processedData = getWeeksOfMonthData(allData, selectedMonth.year, selectedMonth.month);
        }
        break;
    }

    return addWeekIndex(processedData);
  }, [historyData, timeFilter, selectedWeek, selectedMonth]);

  return {
    chartData,
    isLoading,
    error,
  };
};
